import { useMemo } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

export enum ProgressBarVariant {
  GALAXY = "GALAXY",
  PRIMARY = "PRIMARY",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

const StyledProgressBar = withTheme(styled.div<PropsWithTheme<{ $height: number }>>`
  position: relative;
  height: ${({ $height }) => `${$height}px`};
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.tertiary};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  border-radius: 4px;
  overflow: hidden;
`);

const ProgressBarFill = withTheme(styled.div<
  PropsWithTheme<{ $width: number; $variant: ProgressBarVariant; $noAnimation: boolean }>
>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  background: linear-gradient(
    90deg,
    ${({ theme, $variant }) =>
        match($variant)
          .returnType<string>()
          .with(ProgressBarVariant.GALAXY, () => theme.color.background.galaxy)
          .with(ProgressBarVariant.PRIMARY, () => theme.color.opacity.bw100Alt)
          .with(ProgressBarVariant.SUCCESS, () => theme.color.icon.success)
          .with(ProgressBarVariant.ERROR, () => theme.color.icon.error)
          .exhaustive()}
      0%,
    ${({ theme, $variant }) =>
        match($variant)
          .returnType<string>()
          .with(ProgressBarVariant.GALAXY, () => theme.color.background.galaxy)
          .with(ProgressBarVariant.PRIMARY, () => theme.color.opacity.bw80Alt)
          .with(ProgressBarVariant.SUCCESS, () => theme.color.icon.success)
          .with(ProgressBarVariant.ERROR, () => theme.color.icon.error)
          .exhaustive()}
      20%,
    ${({ theme, $variant }) =>
        match($variant)
          .returnType<string>()
          .with(ProgressBarVariant.GALAXY, () => theme.color.background.galaxy)
          .with(ProgressBarVariant.PRIMARY, () => theme.color.opacity.bw48Alt)
          .with(ProgressBarVariant.SUCCESS, () => theme.color.icon.success)
          .with(ProgressBarVariant.ERROR, () => theme.color.icon.error)
          .exhaustive()}
      50%,
    ${({ theme, $variant }) =>
        match($variant)
          .returnType<string>()
          .with(ProgressBarVariant.GALAXY, () => theme.color.background.galaxy)
          .with(ProgressBarVariant.PRIMARY, () => theme.color.opacity.bw80Alt)
          .with(ProgressBarVariant.SUCCESS, () => theme.color.icon.success)
          .with(ProgressBarVariant.ERROR, () => theme.color.icon.error)
          .exhaustive()}
      80%,
    ${({ theme, $variant }) =>
        match($variant)
          .returnType<string>()
          .with(ProgressBarVariant.GALAXY, () => theme.color.background.galaxy)
          .with(ProgressBarVariant.PRIMARY, () => theme.color.opacity.bw100Alt)
          .with(ProgressBarVariant.SUCCESS, () => theme.color.icon.success)
          .with(ProgressBarVariant.ERROR, () => theme.color.icon.error)
          .exhaustive()}
      100%
  );
  background-size: 200% 100%;

  animation: ${({ $noAnimation }) =>
    $noAnimation
      ? "shimmer 2.5s infinite"
      : "shimmer 2.5s infinite, pulse 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite"};

  transition: width 0.075s ease-in-out;

  @keyframes shimmer {
    0% {
      background-position: 150% 0;
    }
    100% {
      background-position: -250% 0;
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.9;
    }
    50% {
      opacity: 0.6;
    }
  }
`);

interface ProgressBarProps {
  percentage: number;
  variant?: ProgressBarVariant;
  height?: number;
  noSliverOnZero?: boolean;
  noAnimation?: boolean;
}

const ProgressBar = ({
  percentage,
  variant = ProgressBarVariant.PRIMARY,
  height = 4,
  noSliverOnZero = false,
  noAnimation = false,
}: ProgressBarProps) => {
  const width = useMemo(() => {
    if (percentage < 0 || percentage > 100) {
      return 0;
    }

    if (percentage === 0 && !noSliverOnZero) {
      return 0.5;
    }

    return percentage;
  }, [percentage, noSliverOnZero]);

  return (
    <StyledProgressBar $height={height}>
      <ProgressBarFill $width={width} $variant={variant} $noAnimation={noAnimation} />
    </StyledProgressBar>
  );
};

export default ProgressBar;
